import React, { memo } from "react";
import { Box, Button, Grid, IconButton, MenuItem, Typography } from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormField from "../FormField";
import ConfigurableField from "../ConfigurableField";
import { FAMILY_RELATIONS } from "../../constants/registrationConstants";
import { CONFIGURED_SECTION_IDS } from "../../utils/sectionConfiguration";
import { useSectionConfig } from "../../context/SectionConfigContext";

const S = CONFIGURED_SECTION_IDS;

function FamilyDetails({ patient, updateListItem, addListItem, removeListItem, errors = {} }) {
  const { canAddListEntry, canRemoveListEntry, config, getListFieldGridSize } = useSectionConfig();
  const md = config ? getListFieldGridSize(S.family) : 3;
  const defaultItem = { name: "", relation: "", phone: "", nextOfKin: false };

  return (
    <Box>
      {patient.family.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", py: 2, border: 1, borderStyle: "dashed", borderRadius: 1, mb: 2 }}>
          Add spouse, parents, children or next-of-kin.
        </Typography>
      )}
      {errors.family__min && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {errors.family__min}
        </Typography>
      )}
      {patient.family.map((m, i) => (
        <Grid container spacing={2} key={i} alignItems="flex-start" sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}>
          <ConfigurableField sectionId={S.family} fieldName="name" xs={12} md={md}>
            {({ required }) => (
              <FormField label="Name" required={required} error={errors[`family.${i}.name`]}>
                <IpdTextField fullWidth size="small" value={m.name} onChange={(e) => updateListItem("family", i, "name", e.target.value)} error={Boolean(errors[`family.${i}.name`])} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.family} fieldName="relation" xs={12} md={md}>
            {({ required }) => (
              <FormField label="Relation" required={required} error={errors[`family.${i}.relation`]}>
                <IpdTextField select fullWidth size="small" value={m.relation} onChange={(e) => updateListItem("family", i, "relation", e.target.value)}>
                  <MenuItem value="">Select</MenuItem>
                  {FAMILY_RELATIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.family} fieldName="phone" xs={12} md={md}>
            {({ required }) => (
              <FormField label="Phone" required={required} error={errors[`family.${i}.phone`]}>
                <IpdTextField fullWidth size="small" value={m.phone} onChange={(e) => updateListItem("family", i, "phone", e.target.value)} error={Boolean(errors[`family.${i}.phone`])} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.family} fieldName="nextOfKin" xs={12} md={md}>
            {({ required }) => (
              <FormField label="Next-of-Kin" required={required}>
                <IpdTextField select fullWidth size="small" value={m.nextOfKin ? "YES" : "NO"} onChange={(e) => updateListItem("family", i, "nextOfKin", e.target.value === "YES")}>
                  <MenuItem value="YES">Yes</MenuItem>
                  <MenuItem value="NO">No</MenuItem>
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          {canRemoveListEntry(S.family, patient.family.length) && (
            <Grid item xs={12} md={1}>
              <IconButton aria-label="Remove family member" onClick={() => removeListItem("family", i)}><DeleteIcon /></IconButton>
            </Grid>
          )}
        </Grid>
      ))}
      {canAddListEntry(S.family, patient.family.length) && (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addListItem("family", { ...defaultItem })}>
          Add Family Member
        </Button>
      )}
    </Box>
  );
}

export default memo(FamilyDetails);
